import React, { useContext, useEffect, useState } from 'react';
import CollaborativeImg from "../../../../Assets/Images/Collaborative-Strategic.png";
import Collaborativesecond from "../../../../Assets/Images/collaborative-second-img.png";
import LeadershipFlowResults from './LeadershipFlowResults';
import HeaderPreview from '../../../GeneratePdf/HeaderPreview';
import FooterPreview from '../../../GeneratePdf/FooterPreview';
// Flow images
import flow_CP from '../../../../Assets/Images/LeadershipFlowImg/flow_CP.png';
import flow_CS from '../../../../Assets/Images/LeadershipFlowImg/flow_CS.png';
import flow_CV from '../../../../Assets/Images/LeadershipFlowImg/flow_CV.png';
import flow_PC from '../../../../Assets/Images/LeadershipFlowImg/flow_PC.png';
import flow_PS from '../../../../Assets/Images/LeadershipFlowImg/flow_PS.png';
import flow_PV from '../../../../Assets/Images/LeadershipFlowImg/flow_PV.png';
import flow_SC from '../../../../Assets/Images/LeadershipFlowImg/flow_SC.png';
import flow_SP from '../../../../Assets/Images/LeadershipFlowImg/flow_SP.png';
import flow_SV from '../../../../Assets/Images/LeadershipFlowImg/flow_SV.png';
import flow_VC from '../../../../Assets/Images/LeadershipFlowImg/flow_VC.png';
import flow_VP from '../../../../Assets/Images/LeadershipFlowImg/flow_VP.png';
import flow_VS from '../../../../Assets/Images/LeadershipFlowImg/flow_VS.png';
import GroupReportContext from '../../../../Context/GroupReportContext';
import { applyFullTieOrder } from '../../../../utils/quadrantTieOrder';

// Hierarchy for tie-breaking: lower number = higher priority
const LEADERSHIP_HIERARCHY = {
  collaborative: 0,  // Highest priority
  visionary: 1,
  strategic: 2,
  performance: 3    // Lowest priority
};

const LABELS = ['performance', 'strategic', 'visionary', 'collaborative'];
const LABEL_SHORT = { strategic: 'S', performance: 'P', visionary: 'V', collaborative: 'C' };
const FLOW_IMAGES = {
  'CP': flow_CP,
  'CS': flow_CS,
  'CV': flow_CV,
  'PC': flow_PC,
  'PS': flow_PS,
  'PV': flow_PV,
  'SC': flow_SC,
  'SP': flow_SP,
  'SV': flow_SV,
  'VC': flow_VC,
  'VP': flow_VP,
  'VS': flow_VS,
};

// Brand Colors
const COLORS = {
  text: {
    primary: '#24242F',
    blue: '#245775'
  },
  quadrants: {
    strategic: 'rgba(36, 87, 117, 1)',      // #245775
    performance: '#C4A127',
    visionary: '#0F5C75',
    collaborative: '#C43827'
  }
};

function getFlowImageName(scores, labels) {
  if (!Array.isArray(scores) || !Array.isArray(labels) || scores.length !== labels.length || scores.length < 2) {
    return "CS";
  }

  const ranked = applyFullTieOrder(
    scores
      .map((score, idx) => ({
        score: Number(score) || 0,
        label: labels[idx]?.toLowerCase?.() || '',
        idx
      }))
      .sort((a, b) => {
        // First compare by score (descending)
        if (b.score !== a.score) return b.score - a.score;
        // If scores are equal, compare by hierarchy (ascending - lower number = higher priority)
        const aHierarchy = LEADERSHIP_HIERARCHY[a.label] ?? 999;
        const bHierarchy = LEADERSHIP_HIERARCHY[b.label] ?? 999;
        return aHierarchy - bHierarchy;
      }),
    (r) => r.label,
    (r) => r.score
  );

  const [highest, secondHighest] = ranked;
  const from = LABEL_SHORT[highest.label];
  const to = LABEL_SHORT[secondHighest.label];

  if (!from || !to) return "CS";
  return `${from}${to}`;
}

export default function LeadershipFlowSecondGroup({ apiData, previewData, reportType, pageNumber }) {

  const { averagePercentagesLeadership } = useContext(GroupReportContext)

  const [leadershipSnapshotResults, setLeadershipSnapshotResults] = useState([]);
  const [leadershipLabels, setLeadershipLabels] = useState([]);
  const [flowImage, setFlowImage] = useState(null);
  const [flowText, setFlowText] = useState('');
  const [flowKey, setFlowKey] = useState('');

  const FLOW_DESCRIPTIONS = {
    'visionary to collaborative': {
      intro: [
        "When Visionary Groups transition to their Collaborative energy, they meld their grand, innovative ideas with a more inclusive and team-oriented approach.",
        "They begin to emphasize the importance of collective input and teamwork for realizing that vision."
      ],
      details: [
        "They recognize that integrating varied insights can enrich their ideas or vision and make implementation more effective and universally acceptable. They want others to feel empowered by their contributions and participation in shaping the direction.",
        "A group may create platforms for open communication where ideas are freely exchanged and developed collectively. This approach not only enhances team cohesion but also ensures that each member feels a personal stake in the success of the collective endeavor."
      ],
    },
    'performance to collaborative': {
      intro: [
        "When Performance Groups transition to include Collaborative leadership, they shift their focus from solely driving high efficiency and achieving targets to emphasizing teamwork, shared goals, and collective input.",
        "Known for their ability to set clear benchmarks and drive results, these groups begin to recognize the benefits of fostering a more inclusive and engaging team environment."
      ],
      details: [
        "They start to balance their goal-oriented approach with an appreciation for the processes that involve team dynamics and relationship-building. They may modify their leadership style to be more open and receptive to feedback, encouraging team members to contribute ideas and share in decision-making.",
        "This shift helps mitigate any tendencies they might have had to view team members as merely operational units, instead acknowledging and valuing each person's unique contributions and perspectives. This approach ensures that while performance remains a priority, it does not overshadow the importance of developing a supportive and flexible team environment."
      ],
    },
    'collaborative to performance': {
      intro: [
        "When a Collaborative Group transitions to Performance leadership, they bring their relational strengths into a focus on efficiency and results.",
        "Initially skilled in fostering environments where team members feel valued and engaged, these leaders want to “get to work.”"
      ],
      details: [
        "Collaborative groups apply their understanding of team dynamics to set clear, achievable goals, ensuring that every member knows their role and can be pushed towards excellence and peak performance. It is important however that the push for results does not compromise the supportive culture they have built.",
        "As Collaborative Groups move into Performance roles, they move from qualitative to quantitative focus, increasing attention on metrics, accountability, and tangible outcomes. They may begin to implement more rigorous monitoring and feedback systems that go beyond interpersonal relationships and dynamics."
      ],
    },
    'strategic to collaborative': {
      intro: [
        "When a Strategic Group adopts a Collaborative approach, the blend manifests as a dynamic leadership style that leverages both the mind’s precision and the heart’s empathy.",
        "This group still crafts robust structures and clear roles but does so with a heightened focus on team dynamics and individual contributions. They shift from a predominantly directive mode to facilitating open dialogues where team members feel valued and part of the decision-making process."
      ],
      details: [
        "By integrating collaborative values, such a group enhances their strategic initiatives with increased buy-in and diversity of thought, fostering an environment where not only are the organizational goals achieved through meticulous planning and execution, but also where a culture of mutual respect and collective effort flourishes.",
        "This dual approach ensures that while the organization’s structure and strategies are rigorously maintained, there is also flexibility and responsiveness to the team's needs and ideas, promoting a balanced and inclusive workplace."
      ],
      last: undefined
    },
    'collaborative to strategic': {
      intro: [
        "When a Collaborative Group transitions into Strategic roles, they leverage their strong foundation in fostering teamwork and mutual respect to implement more structured and goal-oriented strategies.",
        "This shift involves harnessing the trust and communication established in their collaborative role to facilitate effective planning."
      ],
      details: [
        "Collaborative groups already excel in understanding and balancing team dynamics, and as they transition into strategic leadership, they begin to use these insights to define precise roles, responsibilities, and benchmarks that guide the team toward achieving specific outcomes.",
        "These groups can effectively bridge the gap between interpersonal relationships and organizational goals. This combination ensures that strategic initiatives are not only well-planned but also broadly supported. This blended approach ensures that strategic goals are pursued with a deep understanding of and commitment to the human elements that underpin organizational success."
      ],
    },
    'strategic to visionary': {
      intro: [
        "When a Strategic Group combines their energy with Visionary leadership, the fusion creates a powerful style that combines meticulous planning with deep insight.",
        "This group continues to utilize their skills in structuring and organizing, but they begin to infuse every decision with a deeper sense of purpose and a compelling long-term vision."
      ],
      details: [
        "They not only define roles and responsibilities within their teams but also inspire their members by connecting these roles to a larger, meaningful context. This helps transform routine tasks into parts of a grand narrative, which motivates and unites the team.",
        "This hybrid group excels in not just setting clear, pragmatic pathways to success, but also in painting a vivid picture of what success looks like and why it matters. They adeptly use symbols, ceremonies, and storytelling to make the organizational vision resonate with every team member, thus creating a culture that values both efficiency and inspiration."
      ],
    },

    'visionary to performance': {
      intro: [
        "When Visionary Groups transition to Performance leadership, they start to align their broad, forward-thinking visions with the focused pursuit of concrete results.",
        "Known for their ability to see beyond the present and inspire with grand ideas, these leaders want to show them."
      ],
      details: [
        "Visionary groups moving into a performance focus also refine their ability to communicate the practical steps necessary to achieve their vision. They are likely to be adept at setting clear, challenging, yet achievable goals, and motivating their teams to meet these through precise actions and disciplined follow-through.",
        "Moreover, these groups learn to balance their enthusiasm for big-picture thinking with a focus on the day-to-day processes that drive success. These leaders have the opportunity to bridge the gap between dreaming big and acting decisively. This combination of high-level inspiration and ground-level execution can be creatively practical, a rare trait found in most organizations."
      ],
    },
    'visionary to strategic': {
      intro: [
        "When Visionary Groups shift into Strategic roles, they begin to ground their expansive and innovative ideas into practical, actionable plans that can be systematically implemented across the organization.",
        "Initially driven by a deep sense of purpose and a compelling view of the future, these groups evolve to focus more on the detailed steps required to turn their visions into reality."
      ],
      details: [
        "The shift also requires Visionary Groups to enhance their focus on data and metrics. While they continue to inspire with their ideas, they also begin to emphasize the importance of performance indicators, risk assessments, and market analyses to inform their decisions.",
        "This dual focus ensures that their visionary projects are not only inspiring but also viable and aligned with the organization's broader goals. Furthermore, as they transition into Strategic leadership, these leaders learn to balance innovation with risk management. They maintain their inspirational edge but bolster it with a pragmatic focus on execution and sustainability."
      ],
    },
    'performance to strategic': {
      intro: [
        "When Performance Groups transition to Strategic roles, they start to channel their results-driven focus and exceptional execution capabilities towards broader, long-term planning and organizational structuring.",
        "These groups know how to get things done, but they now start to think in terms of systems and processes that are scalable and adaptable, extending their operational excellence to encompass strategic foresight."
      ],
      details: [
        "This transition also demands a new approach to leadership that balances their focus on short-term results with the patience required for strategic planning. They learn to articulate not just what needs to be done but why it matters in the broader scheme of the organization’s future, thereby securing buy-in and commitment from their teams and stakeholders. Ultimately these groups evolve from being highly effective managers of tasks to architects of the organization’s future."
      ],
    },
    'strategic to performance': {
      intro: [
        "When a Strategic Group transitions towards Performance leadership, they harness a robust blend of methodical planning and vigorous execution.",
        "This group still prioritizes strategic frameworks and organizational structures but shifts their focus towards optimizing performance and achieving tangible results swiftly."
      ],
      details: [
        "This blend of strategic and performance leadership emphasizes efficiency and effectiveness, and not just planning the work but also working the plan. These groups become adept at breaking down complex strategies into executable tasks, applying a pragmatic approach to realize strategic visions.",
        "They meticulously manage resources and authority to enhance productivity and customer satisfaction, ensuring the organization’s operations are both smooth and impactful. However, their intensified focus on outcomes and efficiency may sometimes risk reducing team members to mere roles within a process, potentially overlooking individual contributions and personal growth."
      ],
    },
    'collaborative to visionary': {
      intro: [
        "When a Collaborative Group transitions into Visionary leadership, they evolve from fostering a nurturing, team-oriented environment to inspiring those individuals, and possibly the team and organization with a transformative vision.",
        "This shift involves leveraging their innate ability to connect with individuals on a personal level and to help them see beyond daily tasks to the broader impact of their work."
      ],
      details: [
        "While their foundation remains rooted in empathy and cooperation, the scope expands dramatically—envisioning and communicating a future that not only motivates but also galvanizes the entire organization towards shared, lofty goals.",
        "This dual capacity fosters a work environment where the drive towards innovation and legacy is balanced with a deep respect for the collective journey and individual contributions. This blend of heart and soul in leadership ensures that the path to innovation and legacy is pursued with a shared commitment and a deep understanding of collective impact."
      ],
    },
    'performance to visionary': {
      intro: [
        "When Performance Groups integrate Visionary leadership, these leaders start to harness their energy and drive to fuel more transformative ambitions that require not just action but foresight and innovation.",
        "These groups begin to see beyond the operational metrics and performance indicators, focusing instead on how these can serve larger, more impactful goals."
      ],
      details: [
        "This shift often involves embracing more risk and ambiguity than they are accustomed to, as visionary goals typically require venturing into uncharted territories. They learn to articulate a clear and motivating picture of the future, linking everyday tasks to these larger objectives, which infuses their team's work with deeper meaning and purpose.",
        "These groups begin to prioritize innovation and creativity in their strategy execution. They encourage their teams to think outside the box and challenge the status quo, fostering an environment where new ideas are valued and explored. Ultimately, they evolve from managers who excel in driving efficiency to leaders who inspire change."
      ],
    }
  };

  useEffect(() => {
    if (averagePercentagesLeadership) {
      const sortedLeadership = applyFullTieOrder([...averagePercentagesLeadership].sort((a, b) => {
        // First compare by avg (descending)
        const avgDiff = b.avg - a.avg;
        if (avgDiff !== 0) return avgDiff;
        // If averages are equal, compare by hierarchy (ascending - lower number = higher priority)
        const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
        const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
        return aHierarchy - bHierarchy;
      }), (el) => el.name, (el) => el.avg);
      setLeadershipSnapshotResults(sortedLeadership.map((el) => el.avg));
      setLeadershipLabels(sortedLeadership.map((el) => el.name));

      if (sortedLeadership.length >= 2) {
        const flowImg = getFlowImageName(
          sortedLeadership.map(el => el.avg),
          sortedLeadership.map(el => el.name)
        );
        setFlowImage(FLOW_IMAGES[flowImg]);

        const [highest, secondHighest] = sortedLeadership;
        const highestName = highest.name.charAt(0).toUpperCase() + highest.name.slice(1);
        const secondHighestName = secondHighest.name.charAt(0).toUpperCase() + secondHighest.name.slice(1);

        setFlowText(`${highestName} to ${secondHighestName} Group Flow`);
        setFlowKey(`${highest.name.toLowerCase()} to ${secondHighest.name.toLowerCase()}`);
      }
    }
  }, [averagePercentagesLeadership]);

  const flowDescription = FLOW_DESCRIPTIONS[flowKey];

  return (
    <div>
      <HeaderPreview />
      <div className="about-report-section" style={{ padding: '0', fontFamily: 'Helvetica', color: COLORS.text.primary }}>
        <h2 style={{
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '18px',
          fontWeight: 700,
          letterSpacing: '0px',
          marginBottom: '16px',
          color: "#00577D",
        }}>LEADERSHIP FLOW</h2>
        <h4 style={{
          fontSize: '11px',
          fontWeight: 700,
          lineHeight: '15px',
          letterSpacing: '0px',
          marginBottom: '16px',
          color: "#00577D",
          textTransform:"none"
        }}>
          {flowText || 'You flow from ...'}
        </h4>
        <div style={{
          display: 'flex',
          gap: '15px',
          paddingBottom:"16px"
        }}>
          {flowImage && (
            <img
              src={flowImage}
              alt="Flow"
              style={{
                width: "108px",
                height: "108px",
              }}
            />
          )}
          <div style={{}}>
            {Array.isArray(flowDescription?.intro)
              ? flowDescription?.intro.map((paragraph, idx) => (
                <p
                  key={`intro-${idx}`}
                  style={{
                    fontSize: '10px',
                    fontWeight: 400,
                    lineHeight: '14px',
                    letterSpacing: '0px',
                    marginBottom: idx === flowDescription.intro.length - 1 ? '0px' : '16px',
                    color: "#000000",
                  }}
                >
                  {paragraph}
                </p>
              ))
              : (
                <p style={{
                  fontSize: '10px',
                  fontWeight: 400,
                  lineHeight: '14px',
                  letterSpacing: '0px',
                  marginBottom: '16px',
                  color: "#000000",
                }}>
                  {flowDescription?.intro}
                </p>
              )}
          </div>
        </div>

        <div>
          {flowDescription?.details?.map((paragraph, idx) => (
            <p style={{
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '14px',
              letterSpacing: '0px',
              marginBottom: '16px',
              color: "#000000",
            }}>
              {paragraph}
            </p>
          ))}

        </div>


        <div style={{ marginTop: '50px' }}>
          <LeadershipFlowResults
            leadershipSnapshotResults={leadershipSnapshotResults}
            leadershipLabels={leadershipLabels}
            colors={COLORS.quadrants}
          />
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
}
